export const ROLES = ["platform_admin", "authority_admin", "port_manager", "dock_staff", "accountant"] as const;
export type Role = (typeof ROLES)[number];
export const OFFICE_ROLES: readonly Role[] = ["platform_admin", "authority_admin", "port_manager", "accountant"];
export const PORT_SCOPED_ROLES: readonly Role[] = ["port_manager", "dock_staff"];

/**
 * First path segments used by the web app (/admin, /dock, /login, /api, built assets). When the
 * app and the public pages share one hostname, the public Worker forwards these to the API Worker,
 * so no authority may use them as its slug.
 */
export const APP_PATH_SEGMENTS = ["api", "admin", "dock", "login", "assets"] as const;
/** Second path segments the public site uses under an authority (`/{authority}/privatnost`). */
export const PUBLIC_AUTHORITY_PAGES = ["privatnost"] as const;
export const RESERVED_AUTHORITY_SLUGS: readonly string[] = [...APP_PATH_SEGMENTS, "health", "fonts", "static", "widget"];

export const PORT_PART_TYPES = ["operational", "communal", "nautical", "fishing", "sport", "fuel", "service"] as const;
export type PortPartType = (typeof PORT_PART_TYPES)[number];

export const MOORING_MODES = ["stern_to", "side_to", "mixed"] as const;
export type QuayMooringMode = (typeof MOORING_MODES)[number];
export type StayMooringMode = Exclude<QuayMooringMode, "mixed">;

export const BERTH_TYPES = ["permanent", "transit", "reserved", "unavailable"] as const;
export type BerthType = (typeof BERTH_TYPES)[number];

export const VESSEL_TYPES = ["motor", "sail", "multihull", "other"] as const;
export type VesselType = (typeof VESSEL_TYPES)[number];

export const STAY_STATUSES = ["active", "departed", "cancelled"] as const;
export type StayStatus = (typeof STAY_STATUSES)[number];
export const STAY_SOURCES = ["dock", "admin", "import"] as const;
export type StaySource = (typeof STAY_SOURCES)[number];

export const CONTRACT_STATUSES = ["draft", "active", "expired", "terminated"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const WAITING_STATUSES = ["waiting", "offered", "accepted", "declined", "withdrawn"] as const;
export type WaitingStatus = (typeof WAITING_STATUSES)[number];

export const PAYMENT_METHODS = ["cash", "card", "bank_transfer", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
/** Fiskalizacija 1.0 payment method codes (NacinPlac). */
export const FISCAL_PAYMENT_CODES: Record<PaymentMethod, string> = { cash: "G", card: "K", bank_transfer: "T", other: "O" };

export const FISCAL_STATUSES = ["pending", "fiscalized", "failed", "not_required"] as const;
export type FiscalStatus = (typeof FISCAL_STATUSES)[number];
export const ERACUN_STATUSES = ["none", "queued", "sent", "failed"] as const;
export type ERacunStatus = (typeof ERACUN_STATUSES)[number];

export const CONNECTIONS = ["16A", "32A", "63A", "water"] as const;
export type Connection = (typeof CONNECTIONS)[number];

export const IMPORT_TYPES = ["geojson_plan", "csv_tariff", "csv_people", "csv_vessels", "csv_contracts"] as const;
export type ImportType = (typeof IMPORT_TYPES)[number];
/**
 * Who may run each import type: the same roles as the endpoints its rows would otherwise go
 * through. Tariff, plan and contract imports are tied to one port (the upload and the commit check
 * the caller's access to it), so a port manager imports only into their own ports.
 */
export const IMPORT_TYPE_ROLES: Record<ImportType, readonly Role[]> = {
  geojson_plan: ["authority_admin", "port_manager"],
  csv_tariff: ["authority_admin", "port_manager"],
  csv_people: ["authority_admin", "port_manager"],
  csv_vessels: ["authority_admin", "port_manager"],
  csv_contracts: ["authority_admin", "port_manager"],
};

export const LOCALES = ["hr", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const TIME_ZONE = "Europe/Zagreb";

/** Common boat lengths offered as quick chips in the dock app (metres). */
export const QUICK_LENGTHS = [6, 8, 10, 12, 14, 16, 18] as const;

/** Shortest password accepted when a password is set. */
export const PASSWORD_MIN_LENGTH = 8;

/** A sign-in is remembered on the device for this long and renewed while it is in use. */
export const SESSION_DAYS = 30;
